import { getCompany } from "../data/storage/authStorage";

/**
 * ZOUR-19 — does GST apply to THIS document?
 *
 * The browser-side twin of `backend/app/Libraries/GstApplicability.php` and
 * `cockpit-core/public/gst_applicability_lib.php`. Same three modes, same
 * semantics, deliberately the same shape so the three can be read side by side.
 *
 * WHY THE UI RESOLVES THIS AT ALL, rather than being told by the server. Two
 * reasons, and the second is the one that decides it:
 *
 *  1. Every review page already has the company in `localStorage` and the
 *     document's date in the form, so the answer costs nothing.
 *  2. The manual-create pages have to react WHILE THE USER EDITS THE DATE. A
 *     server-resolved answer would need a round trip per keystroke, and until
 *     it came back the tax columns would be showing the wrong thing on a form
 *     someone is filling in.
 *
 * The server is still the authority: it decides the payload that reaches Zoho
 * and it strips the tax whatever the browser drew. This is what the screen
 * shows, not what the books get.
 */

export const GST_MODE_OFF = 0;
export const GST_MODE_ON = 1;
export const GST_MODE_PRE_REGISTRATION = 2;

export type GstMode =
  | typeof GST_MODE_OFF
  | typeof GST_MODE_ON
  | typeof GST_MODE_PRE_REGISTRATION;

/**
 * ZOUR-19 review 12 — the server's GST answer for one note.
 *
 * Credit and debit notes are decided by the PARENT document's date, and only
 * the server can resolve that (it needs the invoices/bills tables). The note
 * GET returns this block, and the review pages render it instead of guessing
 * from whatever the invoice picker happens to have loaded — which is empty for
 * a note created through the API, and is how the debit-note page came to show
 * 18% tax on a note the server had already pushed with none.
 */
export interface GstNoteDecision {
  mode: GstMode;
  label: string;
  applies: boolean;
  is_pre_registration: boolean;
  effective_date: string | null;
  gst_enabled_from: string | null;
  message: string | null;
}

/** The company's GST answer, as the two fields that decide it. */
export interface GstCompanySetting {
  companyGstEnabled?: string | number | null;
  gstEnabledFrom?: string | null;
}

/**
 * ZOUR-92 — the effective "GST applies from" date of each GST registration.
 *
 * A MODULE-LEVEL REGISTRY rather than a parameter threaded through every call,
 * and that is a deliberate trade. The six call sites of this file sit inside
 * render paths that already have the document's `location_id` to hand but do
 * NOT have the locations list — wiring the list through all of them would have
 * meant a hook in four more components and a prop in two, for a value that is
 * constant for the whole session.
 *
 * The server does the fallback (`GET /api/v1/locations/active` returns each
 * row's date already resolved to "its own, else the company's"), so what lands
 * here is the answer, not an input to one.
 *
 * EMPTY UNTIL `useActiveLocations` HAS RUN, and empty forever on a tenant whose
 * backend predates ZOUR-92. Both cases fall back to the company date, which is
 * exactly today's behaviour — so a page that renders before the locations call
 * returns shows what it always showed and corrects itself on the next render.
 * The server remains the authority either way: it strips the tax whatever the
 * browser drew.
 */
const locationGstDates = new Map<string, string>();

/** Primary registrations store their date under this key as well — see below. */
const PRIMARY_LOCATION_KEY = "";

/**
 * Record the per-registration dates. Called by `useActiveLocations`.
 *
 * A document filed under the primary stores NULL rather than the primary's id,
 * so the primary's date is registered under both spellings — the same
 * double-keying the PHP twins do, and for the same reason.
 */
export const rememberLocationGstDates = (
  locations: Array<{
    zoho_location_id?: string | null;
    is_primary?: boolean;
    gst_enabled_from?: string | null;
  }>
): void => {
  locationGstDates.clear();

  locations.forEach((location) => {
    const day = gstDay(location.gst_enabled_from ?? null);
    if (day === null) {
      return;
    }

    const id = (location.zoho_location_id ?? "").trim();
    if (id !== "") {
      locationGstDates.set(id, day);
    }

    if (location.is_primary) {
      locationGstDates.set(PRIMARY_LOCATION_KEY, day);
    }
  });
};

/**
 * The date GST starts applying for one registration, or the company's when this
 * registration has nothing of its own (and when nothing has been loaded yet).
 */
export const gstEnabledFromForLocation = (
  locationId: string | null | undefined,
  company?: GstCompanySetting | null
): string | null => {
  const key = (locationId ?? "").trim();
  const known = locationGstDates.get(key);

  if (known !== undefined) {
    return known;
  }

  const resolved = company === undefined ? getCompany() : company;

  return gstDay(resolved?.gstEnabledFrom ?? null);
};

/**
 * A `YYYY-MM-DD` day out of anything the app holds a date in, or null.
 *
 * `YYYY-MM-DD` compares correctly as a string, so callers compare days without
 * constructing Dates (and without a timezone shifting a document across the
 * boundary at midnight). Datetimes, zero dates and blanks all resolve here.
 */
export const gstDay = (raw: string | null | undefined): string | null => {
  if (raw === null || raw === undefined) {
    return null;
  }

  const value = String(raw).trim();

  if (value === "" || value.startsWith("0000-00-00")) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  // DD/MM/YYYY (and DD-MM-YYYY), parsed EXPLICITLY rather than handed to
  // `new Date()`.
  //
  // The two PHP twins get day-first parsing for free (PHP reads "15-05-2026"
  // that way), but JavaScript's Date does not: "15/05/2026" and "15-05-2026"
  // are both Invalid Date. Leaving it to Date would make this twin answer
  // "no date" for exactly the inputs the other two resolve — and the same
  // document would then show tax on screen while the books got none. All
  // three must read a date identically; that is the whole point of the twins.
  //
  // Ambiguous dates resolve DAY-first ("03/04/2026" is 3 April), matching
  // `zour19_gst_day()` and `GstApplicability::toDay()`.
  const dayFirst = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(value);
  if (dayFirst) {
    const day = Number(dayFirst[1]);
    const month = Number(dayFirst[2]);

    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${dayFirst[3]}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    // Falls through: an out-of-range "month" means this was month-first after
    // all ("05/15/2026"), which `new Date()` below reads correctly — the same
    // last-resort the PHP twins apply.
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  // Local parts, not toISOString(): the latter converts to UTC and can move a
  // date back a day for anyone east of Greenwich — which is everyone using this.
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * The GST mode for one document date.
 *
 * `companyGstEnabled` not 1 → never GST.
 * `gstEnabledFrom` blank    → GST for every date (today's behaviour).
 * otherwise                 → GST iff `documentDate >= gstEnabledFrom`.
 *
 * A document with no usable date falls back to ON, matching both server twins:
 * it keeps the screen behaving as it does today rather than hiding tax columns
 * on a form whose date has not been filled in yet.
 */
export const gstModeForDate = (
  company: GstCompanySetting | null | undefined,
  documentDate: string | null | undefined
): GstMode => {
  if (!company || Number(company.companyGstEnabled ?? 0) !== 1) {
    return GST_MODE_OFF;
  }

  const from = gstDay(company.gstEnabledFrom);
  if (from === null) {
    return GST_MODE_ON;
  }

  const on = gstDay(documentDate);
  if (on === null) {
    return GST_MODE_ON;
  }

  return on >= from ? GST_MODE_ON : GST_MODE_PRE_REGISTRATION;
};

/** The mode for one document date, reading the company out of localStorage. */
export const gstModeForStoredCompany = (
  documentDate: string | null | undefined
): GstMode => gstModeForDate(getCompany(), documentDate);

/**
 * Whether tax columns, tax rates and the ITC picker belong on this document.
 *
 * This is the one value the review and manual-create pages need: it replaces
 * the `companyGstEnabled !== "0"` test they used before, and both non-ON modes
 * answer false — a pre-registration document shows no tax for the same reason
 * a non-GST company's does.
 */
export const gstAppliesToDocument = (
  documentDate: string | null | undefined,
  company?: GstCompanySetting | null
): boolean =>
  (company === undefined
    ? gstModeForStoredCompany(documentDate)
    : gstModeForDate(company, documentDate)) === GST_MODE_ON;

/** True when the document predates the company's own GST registration. */
export const isPreGstRegistration = (
  documentDate: string | null | undefined,
  company?: GstCompanySetting | null
): boolean =>
  (company === undefined
    ? gstModeForStoredCompany(documentDate)
    : gstModeForDate(company, documentDate)) === GST_MODE_PRE_REGISTRATION;

/**
 * ZOUR-92 — the GST mode for one document, on ITS date and ITS registration.
 *
 * The location-aware sibling of `gstModeForDate`, which is left exactly as it
 * is. `For` matches the naming the backend uses for the same pairing
 * (`gstEnabledFromFor`, `billDestinationOfSupplyFor`).
 *
 * A registration with no date of its own — and every registration on a tenant
 * that has not set any — resolves to the company date, so this answers
 * identically to `gstModeForDate` until somebody uses the feature.
 */
export const gstModeForDocumentLocation = (
  documentDate: string | null | undefined,
  locationId: string | null | undefined,
  company?: GstCompanySetting | null
): GstMode => {
  const resolved = company === undefined ? getCompany() : company;

  if (!resolved || Number(resolved.companyGstEnabled ?? 0) !== 1) {
    return GST_MODE_OFF;
  }

  const from = gstEnabledFromForLocation(locationId, resolved);
  if (from === null) {
    return GST_MODE_ON;
  }

  const on = gstDay(documentDate);
  if (on === null) {
    return GST_MODE_ON;
  }

  return on >= from ? GST_MODE_ON : GST_MODE_PRE_REGISTRATION;
};

/**
 * Whether tax belongs on this document, given the registration it is filed
 * under. The location-aware sibling of `gstAppliesToDocument`.
 */
export const gstAppliesToDocumentFor = (
  documentDate: string | null | undefined,
  locationId: string | null | undefined,
  company?: GstCompanySetting | null
): boolean =>
  gstModeForDocumentLocation(documentDate, locationId, company) === GST_MODE_ON;

/**
 * True when the document predates the registration IT IS FILED UNDER. The
 * location-aware sibling of `isPreGstRegistration`.
 */
export const isPreGstRegistrationFor = (
  documentDate: string | null | undefined,
  locationId: string | null | undefined,
  company?: GstCompanySetting | null
): boolean =>
  gstModeForDocumentLocation(documentDate, locationId, company) ===
  GST_MODE_PRE_REGISTRATION;

/** The date GST starts applying, from the stored company. */
export const storedGstEnabledFrom = (): string | null =>
  gstDay(getCompany()?.gstEnabledFrom ?? null);

/**
 * The sentence shown when tax was dropped because the document predates the
 * registration.
 *
 * Kept word-for-word identical to `GstApplicability::preRegistrationMessage()`
 * and `zour19_gst_pre_registration_message()`, so the same document says the
 * same thing on the review page, on the validation flag and in the log.
 */
export const preGstRegistrationMessage = (
  gstEnabledFrom?: string | null
): string => {
  const from = gstDay(gstEnabledFrom ?? null) ?? storedGstEnabledFrom();

  if (from === null) {
    return "GST is not applicable to this document; any tax on it was dropped.";
  }

  return `GST not applicable before ${from}; tax on this document was dropped.`;
};
